import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";
import { isChunkLoadError, tryReloadOnChunkError } from "../lib/chunk-load-error";

interface AppErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

export function AppErrorFallback({ error, resetError }: AppErrorFallbackProps) {
  const isStaleDeploy = isChunkLoadError(error);
  const [isReloading, setIsReloading] = useState(isStaleDeploy);

  useEffect(() => {
    if (!isStaleDeploy) {
      return;
    }

    const didReload = tryReloadOnChunkError(error);
    if (!didReload) {
      setIsReloading(false);
    }
  }, [error, isStaleDeploy]);

  if (isReloading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">
            Updating to the latest version...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-4">
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>
            {isStaleDeploy ? "Update required" : "Something went wrong"}
          </AlertTitle>
          <AlertDescription>
            {isStaleDeploy
              ? "A newer version of the app is available. Please refresh to continue."
              : "An unexpected error occurred while loading this page. You can try again, or refresh the browser if the problem persists."}
            {import.meta.env.DEV ? (
              <p className="mt-2 font-mono text-xs break-all">{String(error)}</p>
            ) : null}
          </AlertDescription>
        </Alert>
        <Button
          onClick={() => {
            if (isStaleDeploy) {
              window.location.reload();
              return;
            }
            resetError();
          }}
        >
          {isStaleDeploy ? "Refresh" : "Try again"}
        </Button>
      </div>
    </div>
  );
}
